import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ArrowLeftIcon,
  GlobeAltIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ShoppingCartIcon,
  ShieldCheckIcon,
  ServerIcon
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import {
  listDomains,
  checkDomainAvailability,
  registerDomain,
  getOperationStatus,
  attachDomain,
  deleteDomain,
  finalizeDns
} from '../services/domainManagementService';
import { getSelfUsername } from '../services/userSelf';

const APP_META = {
  casino: {
    label: 'Casino',
    color: 'from-purple-500 to-pink-500',
    badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300'
  },
  agent: {
    label: 'Agent',
    color: 'from-blue-500 to-cyan-500',
    badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
  }
};

const STATUS_META = {
  AVAILABLE: { color: 'text-green-600 dark:text-green-400', label: 'Live' },
  PENDING_VERIFICATION: { color: 'text-amber-600 dark:text-amber-400', label: 'Pending verification' },
  PENDING_DEPLOYMENT: { color: 'text-amber-600 dark:text-amber-400', label: 'Pending deployment' },
  IN_PROGRESS: { color: 'text-blue-600 dark:text-blue-400', label: 'In progress' },
  CREATING: { color: 'text-blue-600 dark:text-blue-400', label: 'Creating' },
  REQUESTING_CERTIFICATE: { color: 'text-blue-600 dark:text-blue-400', label: 'Requesting certificate' },
  UPDATING: { color: 'text-blue-600 dark:text-blue-400', label: 'Updating' },
  FAILED: { color: 'text-red-600 dark:text-red-400', label: 'Failed' },
  AWAITING_APP_CNAME: { color: 'text-amber-600 dark:text-amber-400', label: 'Awaiting CNAME' }
};

const MAX_DOMAINS_PER_APP = 5; // Amplify's hard cap

// Pending registration ops are persisted to localStorage so polling and the
// auto-attach step survive a page reload / tab close. Anything older than
// MAX_OP_AGE_MS is dropped on hydrate — Route 53 Domains operations that
// haven't resolved in 24h are almost certainly dead and we don't want to keep
// hammering AWS for them.
const ACTIVE_OPS_STORAGE_KEY = 'amplify-agent.modifyDomains.activeOps.v1';
const MAX_OP_AGE_MS = 24 * 60 * 60 * 1000;

function loadPersistedOps() {
  try {
    const raw = localStorage.getItem(ACTIVE_OPS_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - MAX_OP_AGE_MS;
    return parsed
      .filter(
        (op) =>
          op &&
          typeof op.operationId === 'string' &&
          typeof op.domainName === 'string' &&
          typeof op.app === 'string' &&
          typeof op.startedAt === 'number' &&
          op.startedAt > cutoff
      )
      .map((op) =>
        op.status === 'ATTACHING' ? { ...op, status: 'SUCCESSFUL' } : op
      );
  } catch {
    return [];
  }
}

function persistOps(ops) {
  try {
    if (!ops || ops.length === 0) {
      localStorage.removeItem(ACTIVE_OPS_STORAGE_KEY);
    } else {
      localStorage.setItem(ACTIVE_OPS_STORAGE_KEY, JSON.stringify(ops));
    }
  } catch {
    // localStorage may be unavailable (private browsing, quota). Non-fatal —
    // page-close persistence is a best-effort feature, not a correctness one.
  }
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRateLimitError(err) {
  return /rate exceeded|throttl|slow down|TooManyRequests|too many requests/i.test(
    err?.message || ''
  );
}

const ATTACH_LOCK_PREFIX = 'amplify-agent.modifyDomains.attachLock.';
const ATTACH_LOCK_MS = 90 * 1000;

function claimAttach(operationId) {
  try {
    const key = ATTACH_LOCK_PREFIX + operationId;
    const existing = Number(localStorage.getItem(key) || 0);
    if (existing && Date.now() - existing < ATTACH_LOCK_MS) return false;
    localStorage.setItem(key, String(Date.now()));
    return true;
  } catch {
    return true;
  }
}

function releaseAttach(operationId) {
  try {
    localStorage.removeItem(ATTACH_LOCK_PREFIX + operationId);
  } catch {
    // localStorage is best-effort.
  }
}

function StatusPill({ status }) {
  const meta = STATUS_META[status] || {
    color: 'text-gray-600 dark:text-gray-400',
    label: status || 'Unknown'
  };
  return (
    <span className={`text-xs font-medium ${meta.color}`}>
      {meta.label}
    </span>
  );
}

const ModifyDomains = () => {
  const navigate = useNavigate();
  const [isAllowed, setIsAllowed] = useState(null); // null = checking, false = blocked, true = ok
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [data, setData] = useState({ casino: null, agent: null });

  // Delete confirmation state
  // shape: { app, domainName, step: 1|2, typed: '' }
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Buy flow state
  const [buyApp, setBuyApp] = useState('casino');
  const [buyDomain, setBuyDomain] = useState('');
  const [buyDuration, setBuyDuration] = useState(1);
  const [availability, setAvailability] = useState(null);
  const [checking, setChecking] = useState(false);
  const [buying, setBuying] = useState(false);
  // in-flight registrations being polled. Hydrated from localStorage on
  // first render so a registration started in a previous session continues
  // to be polled (and auto-attached) when the page is reopened.
  const [activeOps, setActiveOps] = useState(loadPersistedOps);
  const [retryingKey, setRetryingKey] = useState(null);
  const activeOpsRef = useRef(activeOps);
  activeOpsRef.current = activeOps;

  // Mirror activeOps into localStorage on every change so a tab close
  // doesn't lose the in-flight registration.
  useEffect(() => {
    persistOps(activeOps);
  }, [activeOps]);

  // Access gate — only playwinadmin can actually use this (backend enforces too)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const username = await getSelfUsername();
        if (cancelled) return;
        setIsAllowed(username === 'playwinadmin');
      } catch {
        if (!cancelled) setIsAllowed(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const loadDomains = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    setError(null);
    try {
      const resp = await listDomains();
      if (resp && resp.success && resp.data) {
        setData(resp.data);
      } else {
        throw new Error(resp?.error || 'Failed to load domains');
      }
    } catch (err) {
      setError(err.message || 'Failed to load domains');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (isAllowed) loadDomains();
  }, [isAllowed, loadDomains]);

  // One attach at a time. Amplify rejects a burst of CreateDomainAssociation
  // calls, which is what left registered domains with no app and no DNS.
  const attachWithBackoff = useCallback(async (app, domainName) => {
    const delays = [0, 5000, 12000];
    let lastErr;
    for (let attempt = 0; attempt < delays.length; attempt++) {
      if (delays[attempt]) await sleep(delays[attempt]);
      try {
        return await attachDomain(app, domainName);
      } catch (err) {
        lastErr = err;
        if (!isRateLimitError(err) || attempt === delays.length - 1) throw err;
      }
    }
    throw lastErr;
  }, []);

  // Poll active registration operations every 15s. The interval reads the
  // latest ops from a ref so a status update does not start a second poll.
  useEffect(() => {
    let cancelled = false;

    const tick = async () => {
      const snapshot = activeOpsRef.current.filter(
        (op) => op.status !== 'ATTACH_FAILED' && op.status !== 'ATTACHING'
      );
      for (const op of snapshot) {
        if (cancelled) return;
        try {
          const resp = await getOperationStatus(op.operationId);
          if (!resp?.success || cancelled) continue;
          const status = resp.status;
          if (status.status === 'SUCCESSFUL') {
            if (!claimAttach(op.operationId)) continue;
            setActiveOps((prev) =>
              prev.map((o) =>
                o.operationId === op.operationId
                  ? { ...o, status: 'ATTACHING', message: '' }
                  : o
              )
            );
            try {
              const att = await attachWithBackoff(op.app, op.domainName);
              releaseAttach(op.operationId);
              if (cancelled) return;
              setSuccess(
                `Registered & attached ${op.domainName} to ${APP_META[op.app].label}. ` +
                  (att?.dns?.message || '')
              );
              setActiveOps((prev) =>
                prev.filter((o) => o.operationId !== op.operationId)
              );
              loadDomains(true);
            } catch (attErr) {
              releaseAttach(op.operationId);
              if (cancelled) return;
              const message = attErr.message || 'Attach failed';
              setActiveOps((prev) =>
                prev.map((o) =>
                  o.operationId === op.operationId
                    ? { ...o, status: 'ATTACH_FAILED', message }
                    : o
                )
              );
              setError(
                `Registered ${op.domainName} but attach failed: ${message}. Use Retry on that domain.`
              );
            }
          } else if (status.status === 'ERROR' || status.status === 'FAILED') {
            setError(
              `Registration of ${op.domainName} failed: ${status.message || status.status}`
            );
            setActiveOps((prev) =>
              prev.filter((o) => o.operationId !== op.operationId)
            );
          } else {
            setActiveOps((prev) =>
              prev.map((o) =>
                o.operationId === op.operationId
                  ? { ...o, status: status.status, message: status.message }
                  : o
              )
            );
          }
        } catch (err) {
          console.warn('Poll error:', err);
        }
      }
    };

    const id = setInterval(tick, 15000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [attachWithBackoff, loadDomains]);

  // --- Handlers ------------------------------------------------------------

  const handleCheckAvailability = async () => {
    setError(null);
    setAvailability(null);
    const name = buyDomain.trim().toLowerCase();
    if (!name) return;
    setChecking(true);
    try {
      const resp = await checkDomainAvailability(name);
      if (!resp?.success) throw new Error(resp?.error || 'Check failed');
      setAvailability(resp.result);
    } catch (err) {
      setError(err.message || 'Failed to check availability');
    } finally {
      setChecking(false);
    }
  };

  const handleBuy = async () => {
    setError(null);
    setSuccess(null);
    const name = buyDomain.trim().toLowerCase();
    if (!name) return;

    // Final client-side gate: Amplify hard-limits 5 domain associations per app.
    const count = data[buyApp]?.associations?.length || 0;
    if (count >= MAX_DOMAINS_PER_APP) {
      setError(
        `${APP_META[buyApp].label} already has ${count} domain(s). Amplify allows a maximum of ${MAX_DOMAINS_PER_APP} per app. Remove one before buying another.`
      );
      return;
    }

    const priceLabel =
      availability?.price
        ? `${availability.price.amount} ${availability.price.currency}`
        : 'unknown price';
    const confirmed = window.confirm(
      `Buy "${name}" for ${APP_META[buyApp].label} (${priceLabel}/year, ${buyDuration} year${
        buyDuration > 1 ? 's' : ''
      })?\n\nThis will charge the AWS account. WHOIS privacy will be enabled.`
    );
    if (!confirmed) return;

    setBuying(true);
    try {
      const resp = await registerDomain(buyApp, name, buyDuration);
      if (!resp?.success) throw new Error(resp?.error || 'Registration failed');
      setActiveOps((prev) => [
        ...prev,
        {
          operationId: resp.operationId,
          domainName: name,
          app: buyApp,
          startedAt: Date.now(),
          status: 'SUBMITTED'
        }
      ]);
      setBuyDomain('');
      setAvailability(null);
      setSuccess(
        `Registration submitted for "${name}". It typically takes a few minutes; we'll auto-attach it to ${APP_META[buyApp].label} once complete.`
      );
    } catch (err) {
      setError(err.message || 'Failed to register domain');
    } finally {
      setBuying(false);
    }
  };

  const handleRetryAttach = async (app, domainName, operationId) => {
    const key = operationId || `${app}:${domainName}`;
    setRetryingKey(key);
    setError(null);
    setSuccess(null);
    try {
      const att = await attachWithBackoff(app, domainName);
      setSuccess(
        `Attached ${domainName} to ${APP_META[app].label}. ` +
          (att?.dns?.message || '')
      );
      if (operationId) {
        releaseAttach(operationId);
        setActiveOps((prev) => prev.filter((o) => o.operationId !== operationId));
      }
      loadDomains(true);
    } catch (err) {
      const message = err.message || 'Attach failed';
      if (operationId) {
        setActiveOps((prev) =>
          prev.map((o) =>
            o.operationId === operationId
              ? { ...o, status: 'ATTACH_FAILED', message }
              : o
          )
        );
      }
      setError(message);
    } finally {
      setRetryingKey(null);
    }
  };

  const handleRefreshDns = async (app, domainName) => {
    setError(null);
    setSuccess(null);
    try {
      const resp = await finalizeDns(app, domainName);
      setSuccess(resp?.dns?.message || 'DNS refreshed');
      loadDomains(true);
    } catch (err) {
      setError(err.message || 'DNS refresh failed');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    if (deleteTarget.typed !== deleteTarget.domainName) return;
    setDeleting(true);
    setError(null);
    setSuccess(null);
    try {
      const resp = await deleteDomain(
        deleteTarget.app,
        deleteTarget.domainName,
        deleteTarget.typed
      );
      if (!resp?.success) throw new Error(resp?.error || 'Delete failed');
      setSuccess(
        resp.message ||
          `Removed ${deleteTarget.domainName} from ${APP_META[deleteTarget.app].label} and Route 53.`
      );
      setDeleteTarget(null);
      loadDomains(true);
    } catch (err) {
      setError(err.message || 'Failed to delete domain');
    } finally {
      setDeleting(false);
    }
  };

  // --- Gates ---------------------------------------------------------------

  if (isAllowed === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-teal-500 border-t-transparent" />
      </div>
    );
  }

  if (isAllowed === false) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center">
        <ShieldCheckIcon className="w-12 h-12 text-red-500 mb-3" />
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">
          Access denied
        </h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          Domain management is restricted.
        </p>
        <button
          onClick={() => navigate(-1)}
          className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
        >
          Back
        </button>
      </div>
    );
  }

  // --- Render --------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 py-6 sm:py-10 px-3 sm:px-6 lg:px-8">
      <div className="max-w-5xl mx-auto">
        <div className="mb-4 sm:mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 transition-colors"
          >
            <ArrowLeftIcon className="w-5 h-5 mr-2" />
            <span>Back</span>
          </button>
        </div>

        <div className="bg-white dark:bg-gray-800 shadow-xl rounded-2xl overflow-hidden">
          <div className="relative">
            <div className="h-20 sm:h-24 bg-gradient-to-r from-teal-500 to-cyan-600 dark:from-teal-600 dark:to-cyan-700" />
            <div className="absolute -bottom-6 left-4 sm:left-8">
              <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl bg-white dark:bg-gray-700 shadow-lg flex items-center justify-center">
                <GlobeAltIcon className="w-8 h-8 sm:w-10 sm:h-10 text-teal-500" />
              </div>
            </div>
          </div>

          <div className="px-4 sm:px-6 lg:px-8 pt-10 sm:pt-12 pb-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-6">
              <div className="min-w-0">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-1">
                  Modify Domains
                </h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Manage custom domains for the Casino and Agent apps. Buying a domain
                  enables WHOIS privacy and sets up apex + <code>www</code> forwarding
                  automatically.
                </p>
              </div>
              <button
                onClick={() => loadDomains(true)}
                disabled={refreshing || loading}
                className="self-start sm:self-auto shrink-0 flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-200 disabled:opacity-50"
              >
                <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 flex items-start gap-2">
                <XCircleIcon className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                <div className="text-sm text-red-700 dark:text-red-300 flex-1 break-words">
                  {error}
                </div>
                <button
                  onClick={() => setError(null)}
                  className="text-red-500 hover:text-red-700"
                  aria-label="dismiss"
                >
                  ×
                </button>
              </div>
            )}

            {success && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 flex items-start gap-2">
                <CheckCircleIcon className="w-5 h-5 text-green-500 shrink-0 mt-0.5" />
                <div className="text-sm text-green-700 dark:text-green-300 flex-1 break-words">
                  {success}
                </div>
                <button
                  onClick={() => setSuccess(null)}
                  className="text-green-500 hover:text-green-700"
                  aria-label="dismiss"
                >
                  ×
                </button>
              </div>
            )}

            {activeOps.some((op) => op.status !== 'ATTACH_FAILED') && (
              <div className="mb-6 p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                <h3 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-2 flex items-center gap-2">
                  <ArrowPathIcon className="w-4 h-4 animate-spin" />
                  Registrations in progress (
                  {activeOps.filter((op) => op.status !== 'ATTACH_FAILED').length})
                </h3>
                <ul className="space-y-3">
                  {activeOps
                    .filter((op) => op.status !== 'ATTACH_FAILED')
                    .map((op) => {
                    const msg = (op.message || '').trim();
                    const needsEmail = /validation|verify|reachab|registrant/i.test(msg);
                    return (
                      <li
                        key={op.operationId}
                        className="text-xs text-blue-700 dark:text-blue-300"
                      >
                        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5">
                          <span className="font-mono break-all min-w-0">{op.domainName}</span>
                          <span className="shrink-0">
                            {APP_META[op.app]?.label || op.app} ·{' '}
                            {op.status === 'ATTACHING' ? 'Attaching' : op.status || 'SUBMITTED'}
                          </span>
                        </div>
                        {msg && (
                          <div className="mt-1 text-[11px] leading-snug text-blue-800/80 dark:text-blue-200/80 italic break-words">
                            {msg}
                          </div>
                        )}
                        {needsEmail && (
                          <div className="mt-1 text-[11px] leading-snug font-medium text-amber-700 dark:text-amber-300">
                            Action required: open the inbox of the registrant
                            email and click the verification link from
                            noreply@registrar.amazon.com (check spam too).
                            Registration stays paused until that link is
                            clicked.
                          </div>
                        )}
                      </li>
                    );
                  })}
                </ul>
                <p className="mt-3 text-[11px] text-blue-700/70 dark:text-blue-300/70">
                  .com registrations typically take 5–15 minutes once any
                  required verification is done; first-time registrant emails
                  can take longer because of ICANN verification.
                </p>
              </div>
            )}

            {activeOps.some((op) => op.status === 'ATTACH_FAILED') && (
              <div className="mb-6 p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                <h3 className="text-sm font-semibold text-amber-800 dark:text-amber-200 mb-2 flex items-center gap-2">
                  <ExclamationTriangleIcon className="w-4 h-4" />
                  Registered, not attached yet
                </h3>
                <ul className="space-y-3">
                  {activeOps
                    .filter((op) => op.status === 'ATTACH_FAILED')
                    .map((op) => {
                      const busy = retryingKey === op.operationId;
                      return (
                        <li key={op.operationId} className="text-xs text-amber-800 dark:text-amber-200">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <span className="font-mono break-all min-w-0">{op.domainName}</span>
                            <span className="shrink-0">
                              {APP_META[op.app]?.label || op.app}
                            </span>
                          </div>
                          {op.message && (
                            <div className="mt-1 text-[11px] leading-snug break-words">
                              {op.message}
                            </div>
                          )}
                          <div className="mt-2 flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() =>
                                handleRetryAttach(op.app, op.domainName, op.operationId)
                              }
                              disabled={busy}
                              className="text-xs px-3 py-1.5 rounded-md bg-amber-600 hover:bg-amber-700 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {busy ? 'Retrying…' : 'Retry'}
                            </button>
                            <button
                              type="button"
                              onClick={() =>
                                setActiveOps((prev) =>
                                  prev.filter((o) => o.operationId !== op.operationId)
                                )
                              }
                              disabled={busy}
                              className="text-xs px-3 py-1.5 rounded-md bg-white/70 dark:bg-gray-800 hover:bg-white dark:hover:bg-gray-700 text-amber-800 dark:text-amber-200 disabled:opacity-50"
                            >
                              Dismiss
                            </button>
                          </div>
                        </li>
                      );
                    })}
                </ul>
                <p className="mt-3 text-[11px] text-amber-800/80 dark:text-amber-200/80">
                  Retry attaches the domain once, waits and tries again if
                  Amplify is rate limiting, then writes the DNS records.
                </p>
              </div>
            )}

            {/* Buy a new domain */}
            <div className="mb-8 p-4 sm:p-5 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/40">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-3 flex items-center gap-2">
                <ShoppingCartIcon className="w-5 h-5 text-teal-500" />
                Buy a new domain
              </h2>

              <div className="space-y-2">
                <input
                  type="text"
                  inputMode="url"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  value={buyDomain}
                  onChange={(e) => {
                    setBuyDomain(e.target.value);
                    setAvailability(null);
                  }}
                  placeholder="example.com"
                  className="w-full px-4 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
                <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-2">
                  <select
                    value={buyApp}
                    onChange={(e) => setBuyApp(e.target.value)}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full sm:w-auto"
                  >
                    <option value="casino">Casino</option>
                    <option value="agent">Agent</option>
                  </select>
                  <select
                    value={buyDuration}
                    onChange={(e) => setBuyDuration(parseInt(e.target.value, 10))}
                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 w-full sm:w-auto"
                  >
                    {[1, 2, 3, 5, 10].map((y) => (
                      <option key={y} value={y}>
                        {y} year{y > 1 ? 's' : ''}
                      </option>
                    ))}
                  </select>
                  <button
                    onClick={handleCheckAvailability}
                    disabled={!buyDomain.trim() || checking}
                    className="col-span-2 sm:col-auto sm:ml-auto px-4 py-2 rounded-lg bg-gray-200 dark:bg-gray-600 hover:bg-gray-300 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-100 text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {checking ? 'Checking…' : 'Check availability'}
                  </button>
                </div>
              </div>

              {availability && (
                <div className="mt-3 p-3 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="min-w-0 break-words">
                    <span className="font-mono text-gray-900 dark:text-gray-100 break-all">
                      {availability.domainName}
                    </span>{' '}
                    <span
                      className={
                        availability.availability === 'AVAILABLE'
                          ? 'text-green-600 dark:text-green-400 font-medium'
                          : 'text-red-600 dark:text-red-400 font-medium'
                      }
                    >
                      {availability.availability}
                    </span>
                    {availability.price && (
                      <span className="ml-2 text-gray-500 dark:text-gray-400">
                        · {availability.price.amount} {availability.price.currency}/year
                      </span>
                    )}
                  </div>
                  <button
                    onClick={handleBuy}
                    disabled={
                      buying || availability.availability !== 'AVAILABLE'
                    }
                    className="shrink-0 w-full sm:w-auto px-4 py-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    <ShoppingCartIcon className="w-4 h-4" />
                    {buying
                      ? 'Submitting…'
                      : `Buy for ${APP_META[buyApp].label}`}
                  </button>
                </div>
              )}
            </div>

            {/* Domain lists per app */}
            {loading ? (
              <div className="py-16 flex justify-center">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-teal-500 border-t-transparent" />
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {['casino', 'agent'].map((appKey) => {
                  const bucket = data[appKey];
                  const count = bucket?.associations?.length || 0;
                  const atCap = count >= MAX_DOMAINS_PER_APP;
                  return (
                    <section
                      key={appKey}
                      className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden"
                    >
                      <header
                        className={`px-4 py-3 bg-gradient-to-r ${APP_META[appKey].color} text-white flex items-center justify-between gap-2`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <ServerIcon className="w-5 h-5 shrink-0" />
                          <h2 className="font-semibold shrink-0">
                            {APP_META[appKey].label}
                          </h2>
                          {bucket?.appId && (
                            <span className="hidden sm:inline text-xs opacity-80 font-mono truncate">
                              {bucket.appId}
                            </span>
                          )}
                        </div>
                        <span
                          className={`shrink-0 text-xs px-2 py-0.5 rounded-full ${
                            atCap
                              ? 'bg-red-500/30'
                              : 'bg-white/20'
                          }`}
                        >
                          {count}/{MAX_DOMAINS_PER_APP}
                        </span>
                      </header>

                      <div className="divide-y divide-gray-200 dark:divide-gray-700">
                        {count === 0 ? (
                          <div className="p-6 text-center text-sm text-gray-500 dark:text-gray-400">
                            No domains attached.
                          </div>
                        ) : (
                          bucket.associations.map((assoc) => (
                            <div
                              key={assoc.domainName}
                              className="p-3 sm:p-4 flex flex-col gap-2"
                            >
                              <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0 flex-1">
                                  <div className="font-mono text-sm text-gray-900 dark:text-gray-100 break-all">
                                    {assoc.domainName}
                                  </div>
                                  <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
                                    <StatusPill status={assoc.status} />
                                    {assoc.statusReason && (
                                      <span className="text-xs text-gray-500 dark:text-gray-400 break-words">
                                        {assoc.statusReason}
                                      </span>
                                    )}
                                  </div>
                                  {assoc.subDomains?.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-1">
                                      {assoc.subDomains.map((s) => (
                                        <span
                                          key={`${s.prefix}-${s.branchName}`}
                                          className={`text-xs px-2 py-0.5 rounded-full ${APP_META[appKey].badge}`}
                                        >
                                          {s.prefix === '' ? '@' : s.prefix}
                                          {' → '}
                                          {s.branchName}
                                          {s.verified ? ' ✓' : ''}
                                        </span>
                                      ))}
                                    </div>
                                  )}
                                </div>

                                <button
                                  onClick={() =>
                                    setDeleteTarget({
                                      app: appKey,
                                      domainName: assoc.domainName,
                                      step: 1,
                                      typed: ''
                                    })
                                  }
                                  className="shrink-0 p-2.5 min-h-[40px] min-w-[40px] rounded-lg text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center justify-center"
                                  aria-label={`Remove ${assoc.domainName}`}
                                  title="Remove"
                                >
                                  <TrashIcon className="w-5 h-5" />
                                </button>
                              </div>

                              <div className="flex gap-2 flex-wrap">
                                <button
                                  onClick={() =>
                                    handleRefreshDns(appKey, assoc.domainName)
                                  }
                                  className="text-xs px-3 py-1.5 rounded-md bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
                                >
                                  Refresh DNS
                                </button>
                                {(assoc.status === 'FAILED' ||
                                  assoc.status === 'AWAITING_APP_CNAME' ||
                                  assoc.status === 'PENDING_VERIFICATION') && (
                                  <button
                                    onClick={() =>
                                      handleRetryAttach(appKey, assoc.domainName)
                                    }
                                    disabled={retryingKey === `${appKey}:${assoc.domainName}`}
                                    className="text-xs px-3 py-1.5 rounded-md bg-amber-100 dark:bg-amber-900/30 hover:bg-amber-200 dark:hover:bg-amber-900/50 text-amber-700 dark:text-amber-200 disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    {retryingKey === `${appKey}:${assoc.domainName}`
                                      ? 'Retrying…'
                                      : 'Retry attach'}
                                  </button>
                                )}
                              </div>
                            </div>
                          ))
                        )}

                        {atCap && (
                          <div className="p-3 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 flex items-center gap-2">
                            <ExclamationTriangleIcon className="w-4 h-4" />
                            At Amplify's {MAX_DOMAINS_PER_APP}-domain cap.
                            Remove one to add more.
                          </div>
                        )}
                      </div>
                    </section>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Double-confirmation delete modal */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/60 overflow-y-auto"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div
            className="bg-white dark:bg-gray-800 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md overflow-hidden max-h-[95vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-4 sm:p-5 border-b border-gray-200 dark:border-gray-700 flex items-center gap-3">
              <div className="p-2 rounded-full bg-red-100 dark:bg-red-900/30 shrink-0">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                  Remove domain
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 break-all">
                  {APP_META[deleteTarget.app].label} ·{' '}
                  <span className="font-mono">{deleteTarget.domainName}</span>
                </p>
              </div>
            </div>

            <div className="p-4 sm:p-5 space-y-4">
              {deleteTarget.step === 1 ? (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    This removes{' '}
                    <span className="font-mono font-semibold break-all">
                      {deleteTarget.domainName}
                    </span>{' '}
                    from the{' '}
                    <strong>{APP_META[deleteTarget.app].label}</strong> app and
                    from Route 53. The app stops serving it, the hosted zone is
                    deleted, and if this account registered the name, the
                    registration is deleted too. That is permanent and is not
                    refunded.
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Route 53 emails the registrant contact to approve the
                    registration deletion. Until that email is approved, the
                    domain stays on the account and still counts toward the
                    20-domain limit.
                  </p>
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                    <button
                      onClick={() => setDeleteTarget(null)}
                      className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() =>
                        setDeleteTarget({ ...deleteTarget, step: 2 })
                      }
                      className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium"
                    >
                      Continue
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    To confirm, type the domain name exactly:
                  </p>
                  <div className="font-mono text-sm bg-gray-100 dark:bg-gray-900 rounded-md px-3 py-2 text-gray-800 dark:text-gray-200 break-all">
                    {deleteTarget.domainName}
                  </div>
                  <input
                    type="text"
                    inputMode="url"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    autoFocus
                    value={deleteTarget.typed}
                    onChange={(e) =>
                      setDeleteTarget({
                        ...deleteTarget,
                        typed: e.target.value
                      })
                    }
                    placeholder="Type domain name here"
                    className="w-full px-3 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono focus:outline-none focus:ring-2 focus:ring-red-500"
                  />
                  <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
                    <button
                      onClick={() => setDeleteTarget(null)}
                      disabled={deleting}
                      className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 text-sm disabled:opacity-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleDeleteConfirm}
                      disabled={
                        deleting ||
                        deleteTarget.typed !== deleteTarget.domainName
                      }
                      className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {deleting && (
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                      )}
                      {deleting ? 'Removing…' : 'Remove permanently'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ModifyDomains;
